/* eslint-disable @typescript-eslint/no-misused-promises */
import * as otelApi from '@opentelemetry/api';
import { getBrand, getMarket, KvpsDealerIdSchema } from '@org-avp/models';
import { isActiveCandidatePair, StatsSchema } from './model.js';
import { getMeter } from '../metrics.js';
function getConnectionMetric() {
    return getMeter().createCounter('webrtc_connections_total', {
        description: 'Total number of WebRTC connections'
    });
}
export function startNegotiation(peerConnection, props) {
    handleEventsFromSignalingChannel(peerConnection, props);
    handleEventsFromPeerConnection(peerConnection, props);
    triggerNegotiation(props);
}
function handleEventsFromSignalingChannel(peerConnection, props) {
    handle('sdpAnswer', (answer) => peerConnection.setRemoteDescription(answer), props);
    handle('iceCandidate', async (candidate) => {
        // ICE candidates should only be added to the connection once the remote description is set
        // see also https://w3c.github.io/webrtc-pc/#dom-peerconnection-addicecandidate paragraph 4.1
        await waitForRemoteDescriptionToBeSet(peerConnection);
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    }, props);
    handle('sdpOffer', async (offer) => {
        await peerConnection.setRemoteDescription(offer);
        const answer = await createRTCSessionDescription('answer', peerConnection, props);
        props.signalingChannel.sendSdpAnswer(answer);
    }, props);
}
function waitForRemoteDescriptionToBeSet(peerConnection) {
    if (peerConnection.remoteDescription) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const handler = () => {
            if (peerConnection.remoteDescription) {
                peerConnection.removeEventListener('signalingstatechange', handler);
                resolve();
            }
        };
        peerConnection.addEventListener('signalingstatechange', handler);
    });
}
function handleEventsFromPeerConnection(peerConnection, props) {
    peerConnection.oniceconnectionstatechange = handleIceConnectionStateChange;
    peerConnection.onicecandidate = handleIceCandidate;
    function handleIceConnectionStateChange(event) {
        props.logger.debug('PeerConnection ice connection state change', {
            state: peerConnection.iceConnectionState,
            event
        });
        if (isIceFinished(peerConnection) || isIceConnected(peerConnection)) {
            setTimeout(() => void logSelectedIceCandidatePairStats(props.logger, peerConnection), 200); // Wait to ensure some bytes were sent and we select the correct pair.
        }
    }
    function handleIceCandidate(event) {
        const candidate = event.candidate;
        if (!candidate) {
            props.logger.debug('Received null candidate from peer connection -> ICE candidate gathering finished', { event });
            peerConnection.onicecandidate = null;
        }
        if (candidate) {
            props.logger.debug('Sending local ICE candidate to remote via signaling channel', { candidate });
            props.signalingChannel.sendIceCandidate(candidate);
        }
    }
}
function triggerNegotiation(props) {
    props.logger.info('Triggering WebRTC negotiation');
    triggerSdpOfferFromAVE(props);
}
/* According to the WebRTC spec, the only final states are 'completed' and 'failed'
 * However, the reference implementation, used (at least) by chromium and UE, currently stops at connected:
 * https://bugs.chromium.org/p/webrtc/issues/detail?id=9218
 */
const isIceFinished = (peerConnection) => ['completed', 'failed'].includes(peerConnection.iceConnectionState);
const isIceConnected = (peerConnection) => 'connected' === peerConnection.iceConnectionState;
const handle = (event, handler, props) => {
    props.signalingChannel.on(event, async (payload) => {
        try {
            props.logger.debug(`Received ${event} via signaling channel`, {
                payload
            });
            await handler(payload);
        }
        catch (error) {
            props.logger.error(`Error on handling ${event} from signaling`, {
                error
            });
        }
    });
};
function triggerSdpOfferFromAVE(props) {
    const rtcSessionDescription = new RTCSessionDescription({
        type: 'offer',
        sdp: 'PLAYER_CONNECT'
    });
    props.logger.debug('Sending "fake" SDP offer to signaling server', {
        rtcSessionDescription
    });
    props.signalingChannel.sendSdpOffer(rtcSessionDescription);
}
async function createRTCSessionDescription(kind, peerConnection, props) {
    props.logger.debug(`Creating SDP ${kind}`);
    const rtcSessionDescriptionInit = kind === 'answer'
        ? await peerConnection.createAnswer()
        : await peerConnection.createOffer({
            offerToReceiveAudio: false,
            offerToReceiveVideo: !props.dataChannelOnly
        });
    rtcSessionDescriptionInit.sdp = rtcSessionDescriptionInit.sdp?.replace('useinbandfec=1', 'useinbandfec=1;sprop-stereo=1;stereo=1;maxaveragebitrate=510000');
    props.logger.debug('Setting PeerConnection local description', {
        rtcSessionDescriptionInit
    });
    await peerConnection.setLocalDescription(rtcSessionDescriptionInit);
    if (!peerConnection.localDescription) {
        throw new Error('peerConnection.localDescription is not available but required');
    }
    return peerConnection.localDescription;
}
export async function logSelectedIceCandidatePairStats(logger, peerConnection, tryNumber = 1) {
    const redactCandidate = (c) => ({
        ...c,
        ip: undefined
    });
    if (peerConnection.connectionState !== 'connected')
        return;
    try {
        let activePairFound = false;
        const unparsableCandidatePairs = [];
        const parsedCandidatePairs = [];
        const report = (await peerConnection.getStats());
        report.forEach((s) => {
            const stats = StatsSchema.safeParse(s);
            if (!stats.success) {
                if (s.type === 'candidate-pair') {
                    unparsableCandidatePairs.push(s);
                }
                return;
            }
            if (stats.data.type === 'candidate-pair') {
                parsedCandidatePairs.push(stats.data);
            }
            if (isActiveCandidatePair(stats.data)) {
                const localCandidate = report.get(stats.data.localCandidateId);
                const remoteCandidate = report.get(stats.data.remoteCandidateId);
                logger.info('Found selected ICE candidate pair', {
                    candidatePair: stats.data,
                    localCandidate: redactCandidate(localCandidate),
                    remoteCandidate: redactCandidate(remoteCandidate)
                });
                activePairFound = true;
                if (localCandidate.type === 'local-candidate' && remoteCandidate.type === 'remote-candidate') {
                    recordWebrtcTurnStatsMetric(localCandidate.candidateType, remoteCandidate.candidateType);
                }
            }
        });
        if (activePairFound)
            return;
        logger.warn('Tried to log selected ICE candidate pair, but could not find the pair', {
            unparsableCandidatePairs,
            parsedCandidatePairs
        });
        if (tryNumber < 5) {
            setTimeout(() => void logSelectedIceCandidatePairStats(logger, peerConnection, tryNumber + 1), 500 * 2 ** tryNumber);
        }
        else {
            logger.warn('Giving up active ICE candidate pair logging after 5 tries');
        }
    }
    catch (error) {
        logger.warn('Tried to log selected ICE candidate pair, but could not parse stats', { error });
    }
}
function recordWebrtcTurnStatsMetric(localType, remoteType) {
    const otelCtx = otelApi.context.active();
    // NOTE This context key as well as a suitable getter should be defined in an observability library (e.g. @org-avp/telemetry)
    const kvpsDealerIdKey = otelApi.createContextKey('kvpsDealerId');
    const maybeKvpsDealerId = KvpsDealerIdSchema.safeParse(otelCtx.getValue(kvpsDealerIdKey));
    const market = maybeKvpsDealerId.success ? getMarket(maybeKvpsDealerId.data) : undefined;
    const brand = maybeKvpsDealerId.success ? getBrand(maybeKvpsDealerId.data) : undefined;
    getConnectionMetric().add(1, {
        type: 'TURN',
        kind: parseConnectionType(localType, remoteType),
        kvpsDealerId: maybeKvpsDealerId.success ? maybeKvpsDealerId.data : undefined,
        market,
        brand
    });
}
function parseConnectionType(localType, remoteType) {
    if (localType === 'relay' && remoteType === 'relay') {
        return 'both-relay';
    }
    if (localType === 'relay') {
        return 'local-relay';
    }
    if (remoteType === 'relay') {
        return 'remote-relay';
    }
    return 'no-relay';
}
//# sourceMappingURL=negotiation.js.map